import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';
import { Button } from 'primereact/button';
import { Formik, Form } from 'formik';
import gql from 'graphql-tag';
import apollo from '../../services/apollo';
import { growlErrors } from '../../utils/NotificationUtil';
import { NotificationContext } from '../GrowlNotifications';

const INDIVIDUAL = gql`
  mutation($input: ReportWithAssessmentGenericInput!) {
    generateIndividualReport(input: $input) {
      url
    }
  }
`;

const COUNSELORS_INTERESTS = gql`
  mutation($input: ReportWithAssessmentGenericInput!) {
    generateCounselorsInterestsReport(input: $input) {
      url
    }
  }
`;

const STUDENTS_INTERESTS = gql`
  mutation($input: ReportWithAssessmentGenericInput!) {
    generateStudentInterestsReport(input: $input) {
      url
    }
  }
`;

const INTERESTS_GRAPH = gql`
  mutation($input: ReportWithAssessmentGenericInput!) {
    generateInterestsGraphReport(input: $input) {
      url
    }
  }
`;

const SIMPLIFIED_CC_INTERESTS = gql`
  mutation($input: ReportWithAssessmentGenericInput!) {
    generateSimplifiedCareerCounselingInterestsReport(input: $input) {
      url
    }
  }
`;

const SIMPLIFIED_CC_PERSONALITY = gql`
  mutation($input: ReportWithAssessmentGenericInput!) {
    generateSimplifiedCareerCounselingPersonalityReport(input: $input) {
      url
    }
  }
`;

const CC_INTERESTS = gql`
  mutation($input: ReportWithAssessmentGenericInput!) {
    generateCareerCounselingInterestsReport(input: $input) {
      url
    }
  }
`;
const CC_PERSONALITY = gql`
  mutation($input: ReportWithAssessmentGenericInput!) {
    generateCareerCounselingPersonalityReport(input: $input) {
      url
    }
  }
`;
const SUMMARY = gql`
  mutation($input: ReportWithAssessmentAndJobProfileInput!) {
    generateSummaryReport(input: $input) {
      url
    }
  }
`;
const INTERVIEW = gql`
  mutation($input: ReportWithAssessmentAndJobProfileInput!) {
    generateInterviewReport(input: $input) {
      url
    }
  }
`;
const MANAGEMENT = gql`
  mutation($input: ReportWithAssessmentAndJobProfileInput!) {
    generateManagementReport(input: $input) {
      url
    }
  }
`;
const SUMMARY_STANDARD_JOB = gql`
  mutation($input: ReportWithAssessmentAndStandardJobProfileInput!) {
    generateSummaryReportForStandardJob(input: $input) {
      url
    }
  }
`;
const INTERVIEW_STANDARD_JOB = gql`
  mutation($input: ReportWithAssessmentAndStandardJobProfileInput!) {
    generateInterviewReportForStandardJob(input: $input) {
      url
    }
  }
`;
const MANAGEMENT_STANDARD_JOB = gql`
  mutation($input: ReportWithAssessmentAndStandardJobProfileInput!) {
    generateManagementReportForStandardJob(input: $input) {
      url
    }
  }
`;
const SURVEY_REPORT = gql`
  mutation($input: ReportWithSurveyInput!) {
    generateSurveyReport(input: $input) {
      url
    }
  }
`;
const SUPERLINK_REPORT = gql`
  mutation($input: SuperLinkRankingReportInput!) {
    generateRankingReportForSuperLink(input: $input) {
      url
    }
  }
`;
const EXECUTIVE_REPORT = gql`
  mutation generateExecutiveReport($input: ExecutiveReportInput!) {
    generateExecutiveReport(input: $input) {
      url
    }
  }
`;
const TEAM_REPORT = gql`
  mutation generateTeamReport($input: TeamReportInput!) {
    generateTeamReport(input: $input) {
      url
    }
  }
`;
const COMPARATIVE_REPORT = gql`
  mutation generateComparativeReport($input: ComparativeReportInput!) {
    generateComparativeReport(input: $input) {
      url
    }
  }
`;

const DownloadReportButton = ({ reportParams, type, isDisabled, additionalClass, typeButton }) => {
  const growl = useContext(NotificationContext);
  const { formatMessage } = useIntl();

  const messages = defineMessages({
    INDIVIDUAL: { id: 'report.individual', defaultMessage: 'Individual Report' },
    CC_INTERESTS: { id: 'report.CC.interests', defaultMessage: 'Career Counseling - Interests' },
    CC_PERSONALITY: { id: 'report.CC.personality', defaultMessage: 'Career Counseling - Personality' },
    SUMMARY: { id: 'report.summary', defaultMessage: 'Summary Report' },
    INTERVIEW: { id: 'report.interview', defaultMessage: 'Interview Report' },
    MANAGEMENT: { id: 'report.management', defaultMessage: 'Management Report' },
    SUMMARY_STANDARD_JOB: { id: 'report.summary', defaultMessage: 'Summary Report' },
    INTERVIEW_STANDARD_JOB: { id: 'report.interview', defaultMessage: 'Interview Report' },
    MANAGEMENT_STANDARD_JOB: { id: 'report.management', defaultMessage: 'Management Report' },
    SURVEY_REPORT: { id: 'actions.generate', defaultMessage: 'Generate' },
    SUPERLINK_REPORT: { id: 'actions.generate', defaultMessage: 'Generate' },
    EXECUTIVE_REPORT: { id: 'executive_report.btn_generate', defaultMessage: 'Generate Executive Report' },
    TEAM_REPORT: { id: 'team_report.btn_generate', defaultMessage: 'Generate Team Report' },
    COMPARATIVE_REPORT: { id: 'actions.comparative', defaultMessage: 'Generate' },
    GENERATE: { id: 'actions.generate', defaultMessage: 'Generate' },
    COUNSELORS_INTERESTS_REPORT: { id: 'report.counselors-interests', defaultMessage: 'Counselor Interests Report' },
    STUDENTS_INTERESTS_REPORT: { id: 'report.students-interests', defaultMessage: 'Students Interests Report' },
    INTERESTS_GRAPH_REPORT: { id: 'report.interests-graph', defaultMessage: 'Interests Graph Report' },
    SIMPLIFIED_CC_INTERESTS: {
      id: 'report.simplified-CC.interests',
      defaultMessage: 'Simplified Career Counseling - Interests',
    },
    SIMPLIFIED_CC_PERSONALITY: {
      id: 'report.simplified-CC.personality',
      defaultMessage: 'Simplified Career Counseling - Personality',
    },
  });

  const generateStandardJobReportSQL = async (inputValues, report) => {
    const input = {
      account: inputValues.account,
      assessment: inputValues.assessment,
      standardJobProfileId: inputValues.jobProfile.id,
      superMatchId: inputValues.superMatchId,
      locale: inputValues.locale,
      printerFriendly: inputValues.printerFriendly,
    };

    try {
      const result = await apollo.mutate({
        mutation: report,
        variables: {
          input,
        },
      });
      switch (report) {
        case SUMMARY_STANDARD_JOB: {
          window.open(result.data.generateSummaryReportForStandardJob.url, '_blank');
          break;
        }
        case INTERVIEW_STANDARD_JOB: {
          window.open(result.data.generateInterviewReportForStandardJob.url, '_blank');
          break;
        }
        case MANAGEMENT_STANDARD_JOB: {
          window.open(result.data.generateManagementReportForStandardJob.url, '_blank');
          break;
        }
        default: {
          // do something
        }
      }
    } catch (graphqlResponse) {
      growlErrors(growl, graphqlResponse);
    }
  }

  const generateReportSQL = async (inputValues, report) => {
    try {
      const result = await apollo.mutate({
        mutation: report,
        variables: {
          input: inputValues,
        },
      });
      switch (report) {
        case INDIVIDUAL: {
          window.open(result.data.generateIndividualReport.url, '_blank');
          break;
        }
        case COUNSELORS_INTERESTS: {
          window.open(result.data.generateCounselorsInterestsReport.url, '_blank');
          break;
        }
        case STUDENTS_INTERESTS: {
          window.open(result.data.generateStudentInterestsReport.url, '_blank');
          break;
        }
        case INTERESTS_GRAPH: {
          window.open(result.data.generateInterestsGraphReport.url, '_blank');
          break;
        }
        case SIMPLIFIED_CC_INTERESTS: {
          window.open(result.data.generateSimplifiedCareerCounselingInterestsReport.url, '_blank');
          break;
        }
        case SIMPLIFIED_CC_PERSONALITY: {
          window.open(result.data.generateSimplifiedCareerCounselingPersonalityReport.url, '_blank');
          break;
        }
        case CC_INTERESTS: {
          window.open(result.data.generateCareerCounselingInterestsReport.url, '_blank');
          break;
        }
        case CC_PERSONALITY: {
          window.open(result.data.generateCareerCounselingPersonalityReport.url, '_blank');
          break;
        }
        case SUMMARY: {
          window.open(result.data.generateSummaryReport.url, '_blank');
          break;
        }
        case INTERVIEW: {
          window.open(result.data.generateInterviewReport.url, '_blank');
          break;
        }
        case MANAGEMENT: {
          window.open(result.data.generateManagementReport.url, '_blank');
          break;
        }
        case SURVEY_REPORT: {
          window.open(result.data.generateSurveyReport.url, '_blank');
          break;
        }
        case SUPERLINK_REPORT: {
          window.open(result.data.generateRankingReportForSuperLink.url, '_blank');
          break;
        }
        case EXECUTIVE_REPORT: {
          window.open(result.data.generateExecutiveReport.url, '_blank');
          break;
        }
        case TEAM_REPORT: {
          window.open(result.data.generateTeamReport.url, '_blank');
          break;
        }
        case COMPARATIVE_REPORT: {
          window.open(result.data.generateComparativeReport.url, '_blank');
          break;
        }
        default: {
          // do something
        }
      }
    } catch (graphqlResponse) {
      growlErrors(growl, graphqlResponse);
    }
  };

  const generateReportByType = async (inputValues, reportType) => {
    switch (reportType) {
      case 'INDIVIDUAL': {
        return generateReportSQL(inputValues, INDIVIDUAL);
      }
      case 'COUNSELORS_INTERESTS_REPORT': {
        return generateReportSQL(inputValues, COUNSELORS_INTERESTS);
      }
      case 'STUDENTS_INTERESTS_REPORT': {
        return generateReportSQL(inputValues, STUDENTS_INTERESTS);
      }
      case 'INTERESTS_GRAPH_REPORT': {
        return generateReportSQL(inputValues, INTERESTS_GRAPH);
      }
      case 'SIMPLIFIED_CC_INTERESTS': {
        return generateReportSQL(inputValues, SIMPLIFIED_CC_INTERESTS);
      }
      case 'SIMPLIFIED_CC_PERSONALITY': {
        return generateReportSQL(inputValues, SIMPLIFIED_CC_PERSONALITY);
      }
      case 'CC_INTERESTS': {
        return generateReportSQL(inputValues, CC_INTERESTS);
      }
      case 'CC_PERSONALITY': {
        return generateReportSQL(inputValues, CC_PERSONALITY);
      }
      case 'SUMMARY': {
        return generateReportSQL(inputValues, SUMMARY);
      }
      case 'INTERVIEW': {
        return generateReportSQL(inputValues, INTERVIEW);
      }
      case 'MANAGEMENT': {
        return generateReportSQL(inputValues, MANAGEMENT);
      }
      case 'SUMMARY_STANDARD_JOB': {
        return generateStandardJobReportSQL(inputValues, SUMMARY_STANDARD_JOB);
      }
      case 'INTERVIEW_STANDARD_JOB': {
        return generateStandardJobReportSQL(inputValues, INTERVIEW_STANDARD_JOB);
      }
      case 'MANAGEMENT_STANDARD_JOB': {
        return generateStandardJobReportSQL(inputValues, MANAGEMENT_STANDARD_JOB);
      }
      case 'SURVEY_REPORT': {
        return generateReportSQL(inputValues, SURVEY_REPORT);
      }
      case 'SUPERLINK_REPORT': {
        return generateReportSQL(inputValues, SUPERLINK_REPORT);
      }
      case 'EXECUTIVE_REPORT': {
        return generateReportSQL(inputValues, EXECUTIVE_REPORT);
      }
      case 'TEAM_REPORT': {
        return generateReportSQL(inputValues, TEAM_REPORT);
      }
      case 'COMPARATIVE_REPORT': {
        return generateReportSQL(inputValues, COMPARATIVE_REPORT);
      }
      default: {
        // do something
      }
    }
    return null;
  };

  const generateReport = async () => generateReportByType(reportParams, type);

  const initialValues = {
    ...reportParams,
    'report-type': type,
  };

  return (
    <div className={additionalClass}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (value, { setSubmitting }) => {
          await generateReport(value);
          setSubmitting(false);
        }}
      >
        {({ isSubmitting }) => (
          <Form target="_blank">
            <Button
              type="submit"
              icon={classNames('pi pi-download', { 'pi pi-spin pi-spinner': isSubmitting })}
              label={
                typeButton === 'generate'
                  ? formatMessage(messages.GENERATE)
                  : formatMessage(messages[type.toUpperCase()])
              }
              disabled={isDisabled || isSubmitting}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

DownloadReportButton.propTypes = {
  isDisabled: PropTypes.bool.isRequired,
  type: PropTypes.string.isRequired,
  additionalClass: PropTypes.string.isRequired,
  typeButton: PropTypes.string,
  reportParams: PropTypes.objectOf(PropTypes.any).isRequired,
};

DownloadReportButton.defaultProps = {
  typeButton: '',
};

export default DownloadReportButton;
